import { AfterViewInit, Directive, Inject, Input, Output, OnDestroy, OnInit, OnChanges, EventEmitter } from 'ng-metadata/core';
import * as angular from 'angular';
import './sticky-header.directive.scss';

/**
 * @ngdoc directive
 * @name mwStickyHeader
 * @module mw.components.stickyHeader
 * @param {string} [mw-scroll-container-id] - ID of the container of which the content stream lives in
 * @param {string} [mw-scroll-tolerance] - Vertical scroll tolerance in pixels before hiding
 * @param {string} [mw-stretch-on-stick] - Controls if the header should fill the width of the scrolling container
 * @param {string} [mw-hide-on-scroll-down] - Controls if header should be hidden on scroll down or not
 * @param {string} [mw-hide-offset] - Offset from top in pixels before hide
 * @param {expression} [on-stick] - Function that is called when the header is stuck. $event is a boolean that tells is header is stuck or not.
 * @param {expression} [on-unstick] - Function that is called when the header is unstuck. $event is a boolean that tells is header is stuck or not.
 * @param {expression} [on-show] - Function that is called when the header shows. $event is a boolean that tells is header is hidden or not.
 * @param {expression} [on-hide] - Function that is called when the header hides after it has been stuck. $event is a boolean that tells is header is hidden or not.
 * @restrict A
 *
 * @description
 * `mw-sticky-header` is an attribute directive that will make an element "stick" to the top of a container when scrolled.
 *
 * @usage
 * <hljs hljs-language="html">
 * <div mw-sticky-header>
 * </div>
 * </hljs>
 */
@Directive({
    selector: '[mwStickyHeader]'
})
export class StickyHeaderDirective implements AfterViewInit, OnDestroy, OnInit, OnChanges {
    @Input('<') mwScrollContainerId: string;
    @Input('<') mwScrollTolerance: number;
    @Input('<') mwStretchOnStick: boolean;
    @Input('<') mwHideOnScrollDown: boolean;
    @Input('<') mwHideOffset: number;
    @Output() onStick = new EventEmitter<boolean>();
    @Output() onUnstick = new EventEmitter<boolean>();
    @Output() onShow = new EventEmitter<boolean>();
    @Output() onHide = new EventEmitter<boolean>();

    scrollContainerElement: ng.IAugmentedJQuery;
    placeholderElement: ng.IAugmentedJQuery;
    isStuck: boolean = false;
    isHidden: boolean = false;
    lastKnownScrollY: number = 0;
    classes: any;
    scrollHandler: any;
    resizeHandler: any;

    constructor(@Inject('$element') private $element: ng.IAugmentedJQuery,
                @Inject('$window') private $window: ng.IWindowService,
                @Inject('$timeout') private $timeout: ng.ITimeoutService,
                @Inject('$rootScope') private $rootScope: ng.IRootScopeService) {
        'ngInject';
    }

    ngOnInit() {
        this.mwScrollTolerance = this.mwScrollTolerance ? this.mwScrollTolerance : 5;
        this.mwHideOnScrollDown = this.mwHideOnScrollDown === false ? false : true;
        this.mwHideOffset = this.mwHideOffset ? this.mwHideOffset : 1000;
        this.mwStretchOnStick = this.mwStretchOnStick === true ? true : false;
        this.classes = {
            stuck: 'mw-sticky-header--stuck',
            hide: 'mw-sticky-header--hide',
            show: 'mw-sticky-header--show'
        };

        this.$rootScope.$on('mw-navigation:size-toggled', () => { this.resizeAndPosition() });
    }

    ngAfterViewInit() {
        this.initScrollContainerElement();
        this.scrollHandler = () => { this.update() };
        this.resizeHandler = () => { this.resizeAndPosition() };

        this.scrollContainerElement.bind('scroll', this.scrollHandler );
        this.$window.addEventListener('resize', this.resizeHandler);
    }

    initScrollContainerElement() {
        if (this.mwScrollContainerId) {
            this.scrollContainerElement = angular.element(this.$window.document.getElementById(this.mwScrollContainerId));
        } else {
            this.scrollContainerElement = angular.element(this.$element[0].closest('md-content'));
        }
    }

    ngOnDestroy() {
        this.scrollContainerElement.unbind('scroll', this.scrollHandler);
        this.$window.removeEventListener('resize', this.resizeHandler);
    }

    ngOnChanges(changes) {
        if (changes.mwHideOnScrollDown) {
            if (changes.mwHideOnScrollDown.previousValue === true) {
                this.update(true);
            }
        }
    }

    /**
     * Handles updating the state of the componentd
     */
    update(force?: boolean) {
        if (this.scrollContainerElement && this.scrollContainerElement[0]) {
            let currentScrollY  = this.scrollContainerElement[0].scrollTop;

            if (this.isOutOfBounds(currentScrollY)) {
                // Ignore bouncy scrolling in OSX
                return;
            }

            if (this.shouldStick(currentScrollY)) {
                this.stick();

                if (this.shouldHide(currentScrollY, force)) {
                    this.hide();
                } else if (this.shouldShow(currentScrollY, force)) {
                    this.show();
                }
            } else {
                // 'unstick' the element when it has been scrolled back into view
                this.unstick();
            }

            this.lastKnownScrollY = currentScrollY;
        }
    }

    shouldStick(currentScrollY: number) {
        let scrollingElement = this.isStuck ? this.placeholderElement : this.$element;
        let scrollingElementRect = scrollingElement[0].getBoundingClientRect();
        let stickToTop = this.scrollContainerElement[0].getBoundingClientRect().top;

        // 'stick' as soon as the header top outside the bounds of the scroll container
        return scrollingElementRect.top < stickToTop;
    }

    stick() {
        if (!this.isStuck) {
            let scrollContainerBoundingRect = this.scrollContainerElement[0].getBoundingClientRect();

            this.isStuck = true;

            this.createPlaceholder();

            this.resizeAndPosition();

            //this.$timeout( () => { this.$element.addClass(this.classes.stuck); }, 2000 );
            this.$element.addClass(this.classes.stuck);

            this.onStick.emit(this.isStuck);

            this.$element.bind('mousewheel DOMMouseScroll', function (e) {
                e.stopPropagation();
                e.preventDefault();
            });
        }
    }

    unstick() {
        this.$element.removeClass(this.classes.stuck);
        this.$element.removeAttr('style');
        this.show();

        if (this.placeholderElement) {
            this.placeholderElement.remove();
        }

        this.isStuck = false;
        this.onUnstick.emit(this.isStuck);

        this.$element.unbind('mousewheel DOMMouseScroll');
    }

    createPlaceholder() {
        // Remove the previous placeholder
        if (this.placeholderElement) {
            this.placeholderElement.remove();
        }

        this.placeholderElement = angular.element('<div>');
        this.placeholderElement.css('height', this.getElementHeight(this.$element) + 'px');
        this.placeholderElement.css('width', '100%');

        this.$element.parent().append(this.placeholderElement);
    }

    resizeAndPosition() {
        if (this.isStuck) {
            let scrollContainerBoundingRect = this.scrollContainerElement[0].getBoundingClientRect();
            let placeholderElementBoundingRect = this.placeholderElement[0].getBoundingClientRect();

            this.$element.css('top', scrollContainerBoundingRect.top + 'px');
            if (this.mwStretchOnStick) {
                this.$element
                    .css('left', scrollContainerBoundingRect.left + 'px')
                    .css('right', 0 + 'px');
            } else {
                this.$element
                    .css('left', placeholderElementBoundingRect.left + 'px')
                    .css('width', this.placeholderElement[0].offsetWidth + 'px');
            }
        }
    }

    shouldHide(currentScrollY: number, ignoreTolerance?: boolean) {
        let scrollingDown = currentScrollY >= this.lastKnownScrollY;
        let scrollingPlaceholderElementRect = this.placeholderElement[0].getBoundingClientRect();
        let stickToTop = this.scrollContainerElement[0].getBoundingClientRect().top;
        let pastHideOffset = (scrollingPlaceholderElementRect.top + this.mwHideOffset) < stickToTop;
        let toleranceExceeded =  ignoreTolerance ? true : Math.abs(currentScrollY - this.lastKnownScrollY) >= this.mwScrollTolerance;

        return this.mwHideOnScrollDown && this.isStuck && pastHideOffset && scrollingDown && toleranceExceeded;
    }

    /**
     * Hide the header if it's currently showing and stuck
     */
    hide() {
        if (this.isStuck && !this.isHidden && this.$element[0] && this.$element[0].classList) {
            let classList = this.$element[0].classList;
            classList.add(this.classes.hide);

            this.isHidden = true;
            this.onHide.emit(this.isHidden);
        }
    }

    shouldShow(currentScrollY: number, ignoreTolerance?: boolean) {
        let scrollingUp  = currentScrollY <= this.lastKnownScrollY;
        let toleranceExceeded = ignoreTolerance ? true : Math.abs(currentScrollY - this.lastKnownScrollY) >= this.mwScrollTolerance;

        return !this.mwHideOnScrollDown || (this.isStuck && scrollingUp && toleranceExceeded);
    }

    /**
     * Shows the header if it's currently hidden
     */
    show() {
        if (this.isStuck && this.isHidden && this.$element[0] && this.$element[0].classList) {
            let classList = this.$element[0].classList;
            classList.remove(this.classes.hide);

            this.isHidden = false;
            this.onShow.emit(this.isHidden);
        }
    }

    getElementHeight(elem: ng.IAugmentedJQuery) {
        return Math.max(
            elem[0].offsetHeight,
            elem[0].clientHeight
        );
    }

    getElementContentHeight(elem: ng.IAugmentedJQuery) {
        return Math.max(
            elem[0].scrollHeight,
            elem[0].offsetHeight,
            elem[0].clientHeight
        );
    }

    isOutOfBounds(currentScrollY: number) {
        let pastTop  = currentScrollY < 0;
        let pastBottom = currentScrollY + this.getElementHeight(this.scrollContainerElement) > this.getElementContentHeight(this.scrollContainerElement);

        return pastTop || pastBottom;
    }
}



// WEBPACK FOOTER //
// ./src/lib/components/sticky-header/sticky-header.directive.ts